import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  CircleHelp,
  ListChecks,
  Loader2,
  RefreshCw,
  Sparkles,
  Text,
} from "lucide-react";
import { formatTimecode } from "@rec/shared";
import type { MeetingSummary, SummaryPoint } from "@rec/shared";
import { api } from "@/lib/api";
import { Badge, Button, Card, Skeleton, errorMessage } from "@/components/ui";
import { VersionsMenu } from "@/components/VersionsMenu";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { useServerText } from "@/i18n";

/**
 * The AI summary of a meeting.
 *
 * Generated on demand: the model runs on the workspace's own API key, so this
 * costs the tenant real money and waits to be asked. Once generated it belongs
 * to the workspace — everyone sees the same summary, and anyone can regenerate
 * it.
 *
 * Every line is anchored to the moment it was said. The server resolved those
 * offsets from utterance ids the model returned; the model never emitted a time
 * (CLAUDE.md invariant 3), which is why clicking an action item lands exactly on
 * the sentence where somebody agreed to it rather than approximately near it.
 */

type Tab = "overview" | "decisions" | "actions" | "questions";

// Labels are `summary.tabs.<id>` in the `ai` namespace, translated at render.
const TABS: Array<{
  id: Tab;
  icon: typeof Text;
  /** How many items this tab holds. Overview is prose, so it has no count. */
  count: (s: MeetingSummary) => number | null;
}> = [
  { id: "overview", icon: Text, count: () => null },
  {
    id: "decisions",
    icon: CheckCircle2,
    count: (s) => s.decisions.length,
  },
  {
    id: "actions",
    icon: ListChecks,
    count: (s) => s.actions.length,
  },
  {
    id: "questions",
    icon: CircleHelp,
    count: (s) => s.openQuestions.length,
  },
];

export function SummaryPanel({
  recordingId,
  hasTranscript,
  transcriptLoading = false,
  onSeek,
}: {
  recordingId: string;
  hasTranscript: boolean;
  /**
   * The transcript query is separate, so "no transcript yet" and "not loaded
   * yet" look identical from here. Without this the panel asserted there was
   * nothing to summarise on every single page load, before the answer existed.
   */
  transcriptLoading?: boolean;
  onSeek: (ms: number) => void;
}) {
  const { t } = useTranslation(["ai", "common"]);
  const st = useServerText();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>("overview");

  const summary = useQuery({
    queryKey: ["summary", recordingId],
    queryFn: () => api.getSummary(recordingId),
    // Generation takes tens of seconds and finishes in a worker, so the only
    // way the page learns about it is by asking. Polling stops the moment it
    // is no longer being generated.
    refetchInterval: (query) =>
      query.state.data?.status === "generating" ? 3_000 : false,
  });

  const generate = useMutation({
    mutationFn: () => api.generateSummary(recordingId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["summary", recordingId] }),
  });

  const data = summary.data;
  const generating = data?.status === "generating" || generate.isPending;

  return (
    <Card className="overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-3.5">
        <div className="flex items-center gap-2">
          <Sparkles size={15} className="text-accent" />
          <h2 className="text-[14px] font-semibold text-text">{t("summary.title")}</h2>
          {data?.status === "ready" && data.generatedByModel ? (
            <span className="text-[12px] text-text-faint">
              {data.generatedByModel}
              {data.generatedBy ? ` ${t("summary.askedBy", { name: data.generatedBy })}` : ""}
            </span>
          ) : null}
        </div>

        {/*
          Always reachable, not only when a summary is live: the moment history
          matters most is when the live one failed or was never generated and a
          good older version exists to restore.
        */}
        <SummaryVersions recordingId={recordingId} />
        {hasTranscript || transcriptLoading ? (
          <Button
            variant={data ? "secondary" : "primary"}
            size="sm"
            disabled={generating || transcriptLoading}
            onClick={() => generate.mutate()}
          >
            {generating ? (
              <Loader2 size={13} className="animate-spin" />
            ) : data ? (
              <RefreshCw size={13} />
            ) : (
              <Sparkles size={13} />
            )}
            {generating
              ? t("summary.generating")
              : data
                ? t("summary.regenerate")
                : t("summary.generate")}
          </Button>
        ) : null}
      </div>

      <div className="p-5">
        {summary.isLoading ? (
          <div className="space-y-2.5">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-11/12" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        ) : transcriptLoading ? (
          <div className="space-y-2.5">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        ) : !hasTranscript ? (
          <p className="text-[13px] leading-relaxed text-text-muted">
            {t("summary.noTranscript")}
          </p>
        ) : !data ? (
          <p className="text-[13px] leading-relaxed text-text-muted">
            {t("summary.none")}
          </p>
        ) : data.status === "failed" ? (
          <div className="flex items-start gap-2.5">
            <AlertTriangle size={16} className="mt-0.5 shrink-0 text-danger" />
            <p className="text-[13px] leading-relaxed text-text">
              {data.failureReason ? st(data.failureReason) : t("summary.failed")}
            </p>
          </div>
        ) : (
          <>
            <div className="mb-4 flex flex-wrap gap-1">
              {TABS.map(({ id, icon: Icon, count }) => {
                const n = count(data);
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setTab(id)}
                    aria-pressed={tab === id}
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[12.5px] font-medium transition-colors",
                      tab === id
                        ? "bg-accent-subtle text-accent"
                        : "text-text-muted hover:bg-surface-hover hover:text-text",
                    )}
                  >
                    <Icon size={13} strokeWidth={2} />
                    {t(`summary.tabs.${id}`)}
                    {n !== null ? (
                      <span className="tabular-nums text-text-faint">{n}</span>
                    ) : null}
                  </button>
                );
              })}
            </div>

            {/*
              The previous summary stays readable while a new one is generated.
              Blanking it would make every regenerate look like data loss, and
              worse if generation then fails and there is nothing to fall back to.
            */}
            <div className={cn(generating && "opacity-50 transition-opacity")}>
              <TabContent tab={tab} summary={data} onSeek={onSeek} />
            </div>

            {generating ? (
              <p className="mt-4 flex items-center gap-2 text-[12.5px] text-text-muted">
                <Loader2 size={13} className="animate-spin" />
                {t("summary.reading")}
              </p>
            ) : null}
          </>
        )}

        {generate.error ? (
          <p role="alert" className="mt-3 text-[12.5px] text-danger">
            {errorMessage(generate.error, t("summary.couldNotStart"))}
          </p>
        ) : null}
      </div>
    </Card>
  );
}

function TabContent({
  tab,
  summary,
  onSeek,
}: {
  tab: Tab;
  summary: MeetingSummary;
  onSeek: (ms: number) => void;
}) {
  const { t } = useTranslation(["ai", "common"]);
  if (tab === "overview") {
    return (
      <div className="space-y-4">
        {summary.overview ? (
          <div className="space-y-3">
            {summary.overview.split(/\n{2,}/).map((paragraph, i) => (
              <p key={i} dir="auto" className="text-[13.5px] leading-relaxed text-text">
                {paragraph}
              </p>
            ))}
          </div>
        ) : null}

        {summary.keyPoints.length > 0 ? (
          <div>
            <h3 className="mb-2 text-[12px] font-semibold uppercase tracking-wide text-text-faint">
              {t("summary.keyPoints")}
            </h3>
            <ul className="space-y-1">
              {summary.keyPoints.map((point, i) => (
                <SummaryRow key={i} point={point} onSeek={onSeek} />
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    );
  }

  if (tab === "decisions") {
    return (
      <Section
        items={summary.decisions}
        onSeek={onSeek}
        // Distinguished from "we generated nothing": a meeting that settled
        // nothing is a real and useful finding, and saying so is more honest
        // than an empty box.
        empty={t("summary.noDecisions")}
      />
    );
  }

  if (tab === "actions") {
    if (summary.actions.length === 0) {
      return <Empty>{t("summary.noActions")}</Empty>;
    }
    return (
      <ul className="space-y-1">
        {summary.actions.map((action, i) => (
          <SummaryRow
            key={i}
            point={action}
            onSeek={onSeek}
            meta={
              <>
                {action.owner ? (
                  <Badge tone="accent">{action.owner}</Badge>
                ) : (
                  <Badge tone="warning">{t("summary.unassigned")}</Badge>
                )}
                {action.due ? (
                  <span className="text-[12px] text-text-muted">{action.due}</span>
                ) : null}
              </>
            }
          />
        ))}
      </ul>
    );
  }

  if (summary.openQuestions.length === 0) {
    return <Empty>{t("summary.noQuestions")}</Empty>;
  }
  return (
    <ul className="space-y-1">
      {summary.openQuestions.map((question, i) => (
        <SummaryRow
          key={i}
          point={question}
          onSeek={onSeek}
          meta={
            question.askedBy ? (
              <span className="text-[12px] text-text-muted">
                {t("summary.askedByPerson", { name: question.askedBy })}
              </span>
            ) : null
          }
        />
      ))}
    </ul>
  );
}

function Section({
  items,
  onSeek,
  empty,
}: {
  items: SummaryPoint[];
  onSeek: (ms: number) => void;
  empty: string;
}) {
  if (items.length === 0) return <Empty>{empty}</Empty>;
  return (
    <ul className="space-y-1">
      {items.map((item, i) => (
        <SummaryRow key={i} point={item} onSeek={onSeek} />
      ))}
    </ul>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="py-2 text-[13px] text-text-muted">{children}</p>;
}

/**
 * One line of the summary, linked to the second it was said.
 *
 * A point whose citation did not resolve renders as plain text rather than a
 * dead button. The statement can still be true and worth reading — it simply
 * has nowhere to jump to, and a link that goes to the wrong moment is worse
 * than no link.
 */
function SummaryRow({
  point,
  onSeek,
  meta,
}: {
  point: SummaryPoint;
  onSeek: (ms: number) => void;
  meta?: React.ReactNode;
}) {
  const { t } = useTranslation(["ai", "common"]);
  const body = (
    <>
      <span dir="auto" className="min-w-0 flex-1 text-[13.5px] leading-relaxed text-text">
        {point.text}
      </span>
      <span className="flex shrink-0 items-center gap-2 pt-0.5">
        {meta}
        {point.startMs !== null ? (
          <span className="tabular-nums text-[12px] text-accent">
            {formatTimecode(point.startMs)}
          </span>
        ) : null}
      </span>
    </>
  );

  if (point.startMs === null) {
    return <li className="flex gap-3 px-2 py-1.5">{body}</li>;
  }

  return (
    <li>
      <button
        type="button"
        onClick={() => onSeek(point.startMs!)}
        title={
          point.speaker
            ? t("summary.jumpToSpeaker", {
                speaker: point.speaker,
                time: formatTimecode(point.startMs),
              })
            : t("summary.jumpTo", { time: formatTimecode(point.startMs) })
        }
        className="flex w-full gap-3 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-surface-hover"
      >
        {body}
      </button>
    </li>
  );
}


/**
 * Version history for the summary. Fetched when the menu opens, restored by
 * copying the snapshot back into the live row — the snapshot never changes.
 */
function SummaryVersions({ recordingId }: { recordingId: string }) {
  const st = useServerText();
  const queryClient = useQueryClient();
  const [opened, setOpened] = useState(false);

  const versions = useQuery({
    queryKey: ["summary-versions", recordingId],
    queryFn: () => api.listSummaryVersions(recordingId),
    enabled: opened,
  });

  const restore = useMutation({
    mutationFn: (versionId: string) => api.restoreSummaryVersion(recordingId, versionId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["summary", recordingId] });
      void queryClient.invalidateQueries({ queryKey: ["summary-versions", recordingId] });
    },
  });

  return (
    <VersionsMenu
      items={(versions.data ?? []).map((v) => ({
        id: v.id,
        label: st(v.label),
        detail: v.model,
        createdAt: v.createdAt,
        isLive: v.isLive,
      }))}
      loading={versions.isLoading}
      restoringId={restore.isPending ? (restore.variables ?? null) : null}
      onOpen={() => setOpened(true)}
      onRestore={(id) => restore.mutate(id)}
    />
  );
}
